import GA4EventType from "./GoogleAnalyticEventType";
import { useSelector, useDispatch } from "react-redux";
import {
  trackEvent,
  identifyUser,
  trackUserEngagement
} from "./GoogleAnalytics";
import { invcsConnect } from "../../locales/en";
import { getProfile } from "../../redux/userManagementSlice";
import { getOnboardingData } from "../../redux/loginSlice";

const useGA4EventAction = () => {
  const { GA4_EVENTS } = GA4EventType();
  const dispatch = useDispatch();
  const userIdEvent = useSelector(
    (state) => state?.userManagement?.profile?.id
  );

  const fetchConnectData = async () => {
    try {
      const profileResponse = await dispatch(getProfile()).unwrap();
      const userId = profileResponse?.data?.data?.id;
      const onboardingResponse = await dispatch(getOnboardingData()).unwrap();
      const isSingleTimeAccountingSoftewareConnect =
        onboardingResponse?.data?.data?.isSingleTimeAccountingSoftewareConnect;
      return { userId, isSingleTimeAccountingSoftewareConnect };
    } catch (err) {
      console.error(err);
      return { userId: null, isSingleTimeAccountingSoftewareConnect: null };
    }
  };

  // Configuration
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000;

  const checkEnvironment = () => {
    return true;
  };

  const safeTrack = async (eventName, eventParams, attempt = 1) => {
    try {
      if (!checkEnvironment()) return;

      trackEvent(eventName, {
        ...eventParams,
        timestamp: eventParams.timestamp || new Date().toISOString(),
        attempt_number: attempt
      });
    } catch (error) {
      console.error(
        `${invcsConnect?.invoManualText.useGa4.errMsg.item1} ${attempt}):`,
        error
      );
      if (attempt < MAX_RETRIES) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        return safeTrack(eventName, eventParams, attempt + 1);
      }
      console.error(
        invcsConnect?.invoManualText.useGa4.errMsg.item2,
        eventName
      );
    }
  };

  const signupGA4Event = async (properties = {}, promoCode, referralCode) => {
    if (!properties?.user?.id) {
      console.error(invcsConnect?.invoManualText.useGa4.errMsg.item3);
      return;
    }

    identifyUser(properties.user.id);

    const organicSignupEvent = GA4_EVENTS.ORGANIC_SIGNUP_EVENT;
    const referralSignupEvent = GA4_EVENTS.REFERRAL_SIGNUP_EVENT;
    const demoSignupEvent = GA4_EVENTS.SIGNUP_COMPLETE_DEMO_ACCOUNT;
    const testDemoPattern = /test|demo/i;
    const testEmail = /@mailinator\.com$/i;

    const baseParams = {
      userID: properties.user.id,
      timestamp: new Date().toISOString(),
      ...(promoCode && { promoCode: promoCode }),
      ...(referralCode && { referralCode: referralCode })
    };

    if (properties?.user?.userAccountMeta?.referralCode !== null) {
      await safeTrack(referralSignupEvent.event_name, {
        ...baseParams,
        method: referralSignupEvent.parameters.signup_type
      });
    } else if (
      testEmail.test(properties?.user?.primaryEmail) ||
      testDemoPattern.test(properties?.user?.firstName) ||
      testDemoPattern.test(properties?.user?.lastName)
    ) {
      await safeTrack(demoSignupEvent.event_name, {
        ...baseParams,
        method: demoSignupEvent.parameters.signup_type
      });
    } else {
      await safeTrack(organicSignupEvent.event_name, {
        ...baseParams,
        method: organicSignupEvent.parameters.signup_type
      });
    }
  };

  const loginGA4Event = async (client_id) => {
    if (!client_id) {
      console.error(invcsConnect?.invoManualText.useGa4.errMsg.item4);
      return;
    }

    const loginEvent = GA4_EVENTS.LOGIN_EVENT;
    await safeTrack(loginEvent.event_name, {
      userID: client_id
    });
  };

  const submitInvoiceGA4Event = async (properties = {}) => {
    if (!Array.isArray(properties.quotes)) {
      console.error(invcsConnect?.invoManualText.useGa4.errMsg.item5);
      return;
    }

    const submitInvoiceEvent = GA4_EVENTS.SUBMIT_INVOICE_EVENT;

    for (const quote of properties.quotes) {
      if (!quote.invoiceEntity) continue;

      await safeTrack(submitInvoiceEvent.event_name, {
        user: quote.invoiceEntity.userAccountId,
        invoice: quote.invoiceEntity.invoiceNumber,
        amount: quote.invoiceEntity.totalAmount
      });
    }
  };

  const manualInvoiceUploadGA4Event = async (properties = {}) => {
    if (!properties?.data?.invoiceNumber) {
      console.error(invcsConnect?.invoManualText.useGa4.errMsg.item6);
      return;
    }

    const manualInvoiceUploadEvent = GA4_EVENTS.MANUAL_INVOICE_UPLOAD_EVENT;
    await safeTrack(manualInvoiceUploadEvent.event_name, {
      invoice: properties.data.invoiceNumber,
      timestamp: properties.timestamp || new Date().toISOString()
    });
  };

  const xeroConnectGA4Event = async () => {
    const { userId, isSingleTimeAccountingSoftewareConnect } =
      await fetchConnectData();
    if (!userId || !isSingleTimeAccountingSoftewareConnect) return;

    const connectEvent = GA4_EVENTS.XERO_CONNECT_ACCOUNTING_SOFTWARE;
    await safeTrack(connectEvent.event_name, {
      userID: userId,
      softwareName: connectEvent.parameters.accounting_software
    });
  };

  const myobConnectGA4Event = async () => {
    const { userId, isSingleTimeAccountingSoftewareConnect } =
      await fetchConnectData();
    if (!userId || !isSingleTimeAccountingSoftewareConnect) return;

    const connectEvent = GA4_EVENTS.MYOB_CONNECT_ACCOUNTING_SOFTWARE;
    await safeTrack(connectEvent.event_name, {
      userID: userId,
      softwareName: connectEvent.parameters.accounting_software
    });
  };

  const quickbookConnectGA4Event = async () => {
    const { userId, isSingleTimeAccountingSoftewareConnect } =
      await fetchConnectData();
    if (!userId || !isSingleTimeAccountingSoftewareConnect) return;

    const connectEvent = GA4_EVENTS.QUICKBOOK_CONNECT_ACCOUNTING_SOFTWARE;
    await safeTrack(connectEvent.event_name, {
      userID: userId,
      softwareName: connectEvent.parameters.accounting_software
    });
  };

  const reckonConnectGA4Event = async () => {
    const { userId, isSingleTimeAccountingSoftewareConnect } =
      await fetchConnectData();
    if (!userId || !isSingleTimeAccountingSoftewareConnect) return;

    const connectEvent = GA4_EVENTS.RECKON_CONNECT_ACCOUNTING_SOFTWARE;
    await safeTrack(connectEvent.event_name, {
      userID: userId,
      softwareName: connectEvent.parameters.accounting_software
    });
  };

  const preApprovedClientStatusGA4Event = async (userId, time) => {
    if (!userId) {
      console.error(invcsConnect?.invoManualText.useGa4.errMsg.item11);
      return;
    }

    const statusEvent = GA4_EVENTS.CLIENT_STATUS_PRE_APPROVED;
    await safeTrack(statusEvent.event_name, {
      userID: userId,
      timestamp: time || new Date().toISOString()
    });
  };

  const approvedClientStatusGA4Event = async (userId, time) => {
    if (!userId) {
      console.error(invcsConnect?.invoManualText.useGa4.errMsg.item12);
      return;
    }

    const statusEvent = GA4_EVENTS.CLIENT_STATUS_APPROVED;
    await safeTrack(statusEvent.event_name, {
      userID: userId,
      timestamp: time || new Date().toISOString()
    });
  };

  const idVerificationGA4Event = async (userId) => {
    if (!userId) {
      console.error(invcsConnect?.invoManualText.useGa4.errMsg.item13);
      return;
    }

    await safeTrack("idComplete", {
      userID: userId
    });
  };

  // TODO invoiceFullyFunded, clientStatusApproved and clientStatusPreApproved are all
  // misattributed. They fire from admin screens, so GA4 credits the staff member who did the
  // work rather than the customer. They belong on the server-side conversion endpoint
  // (POST /api/v1/int/conversions in integration-service), reported from the service that
  // performs the funding or status change.
  const invoiceFullyFundedGA4Event = async (userId, invoiceID, amount) => {
    if (!userId || !invoiceID || amount === undefined) {
      console.error(invcsConnect?.invoManualText.useGa4.errMsg.item14);
      return;
    }

    await safeTrack("invoiceFullyFunded", {
      userID: userId,
      invoiceID: invoiceID,
      amount: amount
    });
  };

  const submitDemoInvoiceGA4Event = async (userId, invoices = {}) => {
    if (!Array.isArray(invoices)) {
      console.error(invcsConnect?.invoManualText.useGa4.errMsg.item15);
      return;
    }

    for (const invoice of invoices) {
      if (!invoice) continue;
      await safeTrack("submitDemoInvoice", {
        userID: userId,
        invoice: invoice?.demoInvoiceId
      });
    }
  };

  const userEngagementGA4Event = async () => {
    if (!userIdEvent) {
      console.error(invcsConnect?.invoManualText.useGa4.errMsg.item16);
      return;
    }

    trackUserEngagement(userIdEvent);
  };

  const fileDownloadGA4Event = async (userId, fileName) => {
    if (!userId || !fileName) {
      console.error(invcsConnect?.invoManualText.useGa4.errMsg.item17);
      return;
    }

    await safeTrack("file_Download", {
      userID: userId,
      file_name: fileName,
      file_extension: fileName.split(".").pop()
    });
  };

  return {
    signupGA4Event,
    loginGA4Event,
    submitInvoiceGA4Event,
    xeroConnectGA4Event,
    myobConnectGA4Event,
    quickbookConnectGA4Event,
    reckonConnectGA4Event,
    preApprovedClientStatusGA4Event,
    approvedClientStatusGA4Event,
    manualInvoiceUploadGA4Event,
    invoiceFullyFundedGA4Event,
    idVerificationGA4Event,
    submitDemoInvoiceGA4Event,
    fileDownloadGA4Event,
    userEngagementGA4Event
  };
};

export default useGA4EventAction;
